// A compact cassette drawn in aqua lines (the display's colour and glow), for the black tape
// screens: body, screws, label with the tape's name, a window with two spoked hubs that turn while
// it plays, tape packs that wind from left to right as the tape goes on, and the tape running
// past the head opening. Pure SVG + CSS (no images); calm when the OS asks for reduced motion.

export function Cassette({ playing, progress, label }: { playing: boolean; progress: number; label: string }) {
  const p = Math.max(0, Math.min(1, progress));
  const rL = 13 + 15 * (1 - p);
  const rR = 13 + 15 * p;
  const hub = (cx: number) => (
    <g className="cassette-hub" style={{ transformOrigin: `${cx}px 76px` }}>
      <circle cx={cx} cy={76} r={9} />
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <line
          key={a}
          x1={cx + 4 * Math.cos((a * Math.PI) / 180)}
          y1={76 + 4 * Math.sin((a * Math.PI) / 180)}
          x2={cx + 9 * Math.cos((a * Math.PI) / 180)}
          y2={76 + 9 * Math.sin((a * Math.PI) / 180)}
        />
      ))}
    </g>
  );
  return (
    <svg
      className={`cassette${playing ? " playing" : ""}`}
      viewBox="0 0 240 150"
      role="img"
      aria-label={playing ? `Tape playing: ${label}` : `Tape: ${label}`}
      data-testid="cassette"
      data-playing={playing}
    >
      {/* Body and screws */}
      <rect x={3} y={3} width={234} height={144} rx={11} />
      {[
        [13, 13],
        [227, 13],
        [13, 137],
        [227, 137],
      ].map(([x, y]) => (
        <circle key={`${x}${y}`} cx={x} cy={y} r={3} className="faint" />
      ))}
      {/* Label with lines and the name */}
      <rect x={20} y={14} width={200} height={94} rx={7} />
      <line x1={30} y1={40} x2={210} y2={40} className="faint" />
      <text x={120} y={32} textAnchor="middle" className="cassette-name">
        {label.length > 30 ? `${label.slice(0, 29)}…` : label}
      </text>
      <text x={30} y={102} className="cassette-side">
        A
      </text>
      <text x={210} y={102} textAnchor="end" className="cassette-side">
        C-32
      </text>
      {/* Window: tape packs and hubs */}
      <rect x={52} y={52} width={136} height={48} rx={6} className="faint" />
      <clipPath id="cassette-window">
        <rect x={52} y={52} width={136} height={48} rx={6} />
      </clipPath>
      <g clipPath="url(#cassette-window)">
        <circle cx={88} cy={76} r={rL} className="cassette-pack" />
        <circle cx={152} cy={76} r={rR} className="cassette-pack" />
      </g>
      {hub(88)}
      {hub(152)}
      {/* Head opening and the tape running past it */}
      <path d="M60 147 L72 120 L168 120 L180 147" className="faint" />
      {[84, 104, 136, 156].map((x) => (
        <circle key={x} cx={x} cy={134} r={3.2} className="faint" />
      ))}
      <path d={`M${88 - 2} ${76 + rL} L84 126 L156 126 L${152 + 2} ${76 + rR}`} className="cassette-tape" fill="none" />
    </svg>
  );
}
